<template>
  <!-- Mobile only. On desktop the left nav names the current page, so a bar
       that repeats it earns nothing; AppShell hides this above 880px. The
       drawer has no other opener on phones, which is why the bar survives
       here — it carries the hamburger, the page title, and the sync pip
       (which would otherwise be locked inside the closed drawer). -->
  <div class="topbar">
    <button
      class="iconbtn menu-btn"
      title="Open menu"
      aria-label="Open menu"
      @click="drawer?.toggle()"
    >
      <Icon name="list" :size="16" />
    </button>

    <div class="crumbs">
      <b>{{ pageTitle }}</b>
    </div>

    <div class="topbar-actions">
      <transition name="topbar-refresh">
        <span
          v-if="isSyncing || (isRefreshing && !bootInProgress)"
          class="topbar-refresh"
          :class="isSyncing ? `is-${syncTone}` : null"
          role="status"
          aria-live="polite"
          :title="isSyncing ? syncMessage : 'Refreshing data from the server'"
        >
          <span class="topbar-refresh-dot" aria-hidden="true" />
          <span class="topbar-refresh-label">{{ refreshPipLabel }}</span>
        </span>
      </transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, type Ref } from 'vue'
import { useRoute } from 'vue-router'
import { useIsFetching } from '@tanstack/vue-query'
import Icon from './Icon.vue'
import { useTransactionSync } from '@/composables/useTransactionSync'
import { bootInProgress } from '@/composables/useBootStatus'

interface SidebarDrawer {
  open: () => void
  close: () => void
  toggle: () => void
  isOpen: Ref<boolean>
}
const drawer = inject<SidebarDrawer | null>('sidebarDrawer', null)

const route = useRoute()

const ROUTE_TITLES: Record<string, string> = {
  Overview: 'Overview',
  ViewExpenses: 'Expenses',
  Expenses: 'Expenses',
  RulesManager: 'Rules',
  Rules: 'Rules',
  ConnectedAccounts: 'Connected Accounts',
  Accounts: 'Connected Accounts',
  Chat: 'Expense AI',
  Profile: 'Settings',
  Settings: 'Settings',
  HouseholdSettings: 'Household',
  Onboarding: 'Onboarding',
}

const pageTitle = computed(() => {
  const name = route.name?.toString()
  return (name && ROUTE_TITLES[name]) || 'LensPoint'
})

// Sync state is owned by the composable and triggered from the sidebar's
// account pill; this bar only narrates it.
const { isSyncing, syncMessage, syncTone } = useTransactionSync()

// Number of TanStack queries currently fetching across the whole app. Used
// to render a small "refreshing" pip so the user sees that background work is
// happening even when cached data is already on screen.
// Held back during boot: the narration card says what's loading, and more
// specifically.
const fetchingCount = useIsFetching()
const isRefreshing = computed(() => fetchingCount.value > 0)

// Live sync narration takes priority; we fall back to the generic
// "Refreshing…" while only background queries fetch.
const refreshPipLabel = computed(() =>
  isSyncing.value ? (syncMessage.value || 'Syncing…') : 'Refreshing…',
)
</script>

<style scoped>
/* Its own surface, floated over the page on a soft shadow rather than pinned
   down by a hairline rule. */
.topbar {
  /* Grows by the status-bar inset and pads its content past it, so the bar's
     own --surface paints the band above the app (see viewport-fit=cover in
     index.html). Zero on every platform that doesn't overlay a system bar, so
     desktop and pre-Android-15 layouts are unchanged. */
  height: calc(56px + env(safe-area-inset-top, 0px));
  border-bottom: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: env(safe-area-inset-top, 0px) 12px 0;
  background: var(--surface);
  box-shadow: var(--shadow-card);
  position: sticky;
  top: 0;
  z-index: 5;
}
.crumbs {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  color: var(--ink-3);
  white-space: nowrap;
}
.crumbs b { color: var(--ink); font-weight: 600; }

.topbar-actions {
  margin-left: auto;
  display: flex;
  gap: 8px;
  align-items: center;
}

.topbar .iconbtn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
}

/* "Refreshing…" pip — appears whenever a sync runs or any TanStack query is
   fetching. Subtle pulse on the dot keeps it visible without competing with
   content. */
.topbar-refresh {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 9px 3px 7px;
  border-radius: 999px;
  font-size: 11px;
  letter-spacing: -0.005em;
  color: var(--ink-3);
  background: var(--surface-2);
  border: 1px solid var(--border);
  font-variant-numeric: tabular-nums;
}
.topbar-refresh-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--ink-3);
  animation: topbar-refresh-pulse 1.2s ease-in-out infinite;
}
.topbar-refresh-label { white-space: nowrap; }
/* Live-sync tones: a calm tick of colour on the dot + label so "done" and
   "failed" read at a glance without shouting. Tokens are theme-aware. */
.topbar-refresh.is-success {
  color: var(--pos-ink);
  border-color: color-mix(in oklch, var(--pos) 35%, var(--border));
}
.topbar-refresh.is-success .topbar-refresh-dot {
  background: var(--pos);
  animation: none;
}
.topbar-refresh.is-error {
  color: var(--neg-ink);
  border-color: color-mix(in oklch, var(--neg) 35%, var(--border));
}
.topbar-refresh.is-error .topbar-refresh-dot {
  background: var(--neg);
  animation: none;
}
@media (max-width: 560px) {
  .topbar-refresh-label { display: none; }
  .topbar-refresh { padding: 5px; }
}
@keyframes topbar-refresh-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%      { opacity: 0.45; transform: scale(0.85); }
}
.topbar-refresh-enter-active,
.topbar-refresh-leave-active { transition: opacity 0.18s ease-out; }
.topbar-refresh-enter-from,
.topbar-refresh-leave-to { opacity: 0; }
</style>
